import { Component, Input, OnInit, ViewChild, ElementRef, AfterViewInit, OnDestroy } from '@angular/core'
import { FormControl, ReactiveFormsModule } from '@angular/forms'
import { Router } from '@angular/router'
import { DatetimeCustomEvent, IonicModule, IonModal, IonInput } from '@ionic/angular'
import { Subscription } from 'rxjs'
import { UtilsService } from 'src/app/services/utils/utils.service'
import { TranslateModule, TranslateService } from '@ngx-translate/core'
import { GoogleMapsService, PlaceResult } from 'src/app/services/google-maps.service'
import { GooglePlacesService, CitySuggestion } from 'src/app/services/google-places.service'
import { CommonModule } from '@angular/common'

@Component({
  selector: 'app-public-input',
  templateUrl: './public-input.component.html',
  styleUrls: ['./public-input.component.scss'],
  standalone: true,
  imports: [CommonModule, IonicModule, ReactiveFormsModule, TranslateModule]
})
export class PublicInputComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('calendar', { static: false }) modal!: IonModal
  @ViewChild('locationInput') locationInput!: ElementRef<HTMLInputElement>
  @ViewChild('textInput') textInput!: IonInput

  constructor(
    private utilsService: UtilsService,
    private translate: TranslateService,
  private googleMapsService: GoogleMapsService,
  private googlePlaces: GooglePlacesService
  ) {}

  @Input() visible: boolean = false
  @Input() disabled: boolean = false
  @Input() inputControl: FormControl | null = null
  @Input() invalid: boolean = false
  @Input() isWeb: boolean = false
  @Input() field: any
  imageUrl: any
  selectedDate: string = ''
  tempDate: string = ''
  datetimeLocale: string = 'es'
  datetimeCancelText: string = ''
  datetimeDoneText: string = ''
  isLocationField: boolean = false
  isLocationSelected: boolean = false // Flag para rastrear si se seleccionó una ubicación
  initialValue: string = ''; // Para almacenar el valor inicial del campo
  hasBeenEdited: boolean = false; // Para saber si el campo ha sido editado
  // Autocomplete (city) support (reuse logic from filter-input)
  locationQuery: string = '';
  locationSuggestions: CitySuggestion[] = [];
  showLocationSuggestions: boolean = false;
  private suggestionsTimer: any;
  private dateValueSubscription?: Subscription;
  private languageChangeSubscription?: Subscription;

  ngOnInit() {
    // Comprobar si este campo es de tipo ubicación
    this.isLocationField = this.field && this.field.formControlName === 'location';

    this.syncDatetimeLocalization()
    this.languageChangeSubscription = this.translate.onLangChange.subscribe(() => {
      this.syncDatetimeLocalization()
    })

    if (this.field && this.field.type === 'date' && this.inputControl?.value) {
      this.syncDateValue(this.inputControl.value);
    }

    if (this.field?.type === 'date' && this.inputControl) {
      this.dateValueSubscription = this.inputControl.valueChanges.subscribe(value => {
        if (!value) {
          this.selectedDate = ''
          this.tempDate = ''
          return
        }

        this.syncDateValue(String(value))
      })
    }

    // Si es un campo de ubicación, guardar el valor inicial para comparar después
    if (this.isLocationField && this.inputControl) {
      this.initialValue = this.inputControl.value || '';

      // Si hay un valor inicial, considerarlo como válido
      if (this.initialValue && this.initialValue.trim() !== '') {
        this.isLocationSelected = true;
      }

      // Suscribirse a los cambios del control para detectar ediciones
      this.inputControl.valueChanges.subscribe(value => {
        // Marcar como editado solo si el valor es diferente al inicial
        if (value !== this.initialValue) {
          this.hasBeenEdited = true;
        }
      });
    }
  }

  ngAfterViewInit() {
  // No longer using googleMapsService.initAutocomplete here;
  // we rely on GooglePlacesService via onLocationInputChange
  }

  ngOnDestroy() {
    if (this.suggestionsTimer) {
      clearTimeout(this.suggestionsTimer);
    }
    this.dateValueSubscription?.unsubscribe()
    this.languageChangeSubscription?.unsubscribe()
    // nothing else to clean
  }

  // --- Autocomplete (location) logic using GooglePlacesService ---
  onLocationInputChange(ev: any) {
    const value = (ev.target?.value || '').trim();
    this.locationQuery = value;

    // If there's no meaningful input, hide suggestions
    if (this.suggestionsTimer) {
      clearTimeout(this.suggestionsTimer);
    }
    if (value.length < 2) {
      this.locationSuggestions = [];
      this.showLocationSuggestions = false;
      return;
    }

    this.suggestionsTimer = setTimeout(() => {
      this.googlePlaces.getCitySuggestions(value).subscribe({
        next: (suggestions: CitySuggestion[]) => {
          this.locationSuggestions = suggestions;
          this.showLocationSuggestions = suggestions.length > 0;
        },
        error: () => {
          this.locationSuggestions = [];
          this.showLocationSuggestions = false;
        }
      });
    }, 300);
  }

  selectLocationSuggestion(s: CitySuggestion) {
    // Set control value and mark as selected
    if (this.inputControl) {
      this.inputControl.setValue(s.mainText);
      this.isLocationSelected = true;
      this.hasBeenEdited = true;
    }
    this.locationQuery = s.mainText;
    this.showLocationSuggestions = false;
  }

  hideLocationSuggestions() {
    setTimeout(() => {
      this.showLocationSuggestions = false;
    }, 200);
  }

  async handleImageUpload() {
    let url = await this.utilsService.takePicture()
    this.imageUrl = url
    this.inputControl?.setValue(this.imageUrl)
  }
  onDateChange(event: DatetimeCustomEvent) {
    const rawValue = event.detail.value as string | undefined;
    if (!rawValue) {
      return;
    }

    this.tempDate = this.normalizeToIsoDate(rawValue)

    if (!this.tempDate) {
      return;
    }

    // Validar que la fecha no sea futura
    const today = this.getTodayIsoDate();

    // Si la fecha es futura, usar la fecha actual
    if (this.tempDate > today) {
      this.tempDate = today;
    }

    this.confirmDate()
  }

  showCalendar() {
    if (this.modal) {
      this.modal.present()
    }
  }

  closeModal() {
    if (this.modal) {
      this.modal.dismiss()
    }
  }

  confirmDate() {
    if (this.tempDate) {
      this.selectedDate = this.formatDisplayDate(this.tempDate)
    }
    this.inputControl?.setValue(this.selectedDate)
    this.closeModal()
  }

  private syncDateValue(value: string) {
    const normalized = this.normalizeDateValue(value)

    if (!normalized) {
      return
    }

    this.tempDate = normalized.iso
    this.selectedDate = normalized.display

    if (this.inputControl?.value !== normalized.display) {
      this.inputControl?.setValue(normalized.display, { emitEvent: false })
    }
  }

  private syncDatetimeLocalization() {
    const language = this.translate.currentLang || this.translate.getDefaultLang() || 'es'

    this.datetimeLocale = language
    this.datetimeCancelText = this.translate.instant('COMMON.CANCEL')
    this.datetimeDoneText = this.translate.instant('COMMON.ACCEPT')
  }

  private normalizeDateValue(value: string): { iso: string; display: string } | null {
    if (!value) {
      return null
    }

    if (this.isDisplayDate(value)) {
      const iso = this.formatIsoDate(value)
      if (!iso) {
        return null
      }

      return {
        iso,
        display: value
      }
    }

    const iso = this.normalizeToIsoDate(value)
    if (!iso) {
      return null
    }

    return {
      iso,
      display: this.formatDisplayDate(iso)
    }
  }

  private normalizeToIsoDate(value: string): string {
    if (!value) {
      return ''
    }

    if (this.isDisplayDate(value)) {
      return this.formatIsoDate(value)
    }

    if (/^\d{4}-\d{2}-\d{2}/.test(value)) {
      return value.slice(0, 10)
    }

    const parsed = new Date(value)
    if (Number.isNaN(parsed.getTime())) {
      return ''
    }

    return this.getLocalIsoDate(parsed)
  }

  private isDisplayDate(value: string): boolean {
    return /^\d{2}\/\d{2}\/\d{4}$/.test(value)
  }

  private formatIsoDate(displayDate: string): string {
    const [day, month, year] = displayDate.split('/')
    if (!day || !month || !year) {
      return ''
    }

    return `${year}-${month}-${day}`
  }

  private formatDisplayDate(isoDate: string): string {
    const [year, month, day] = isoDate.split('T')[0].split('-')
    if (!year || !month || !day) {
      return ''
    }

    return `${day}/${month}/${year}`
  }

  private getTodayIsoDate(): string {
    return this.getLocalIsoDate(new Date())
  }

  private getLocalIsoDate(date: Date): string {
    const year = date.getFullYear()
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const day = String(date.getDate()).padStart(2, '0')

    return `${year}-${month}-${day}`
  }

  // Método público para verificar si se seleccionó una ubicación válida
  isValidLocation(): boolean {
    // Si no es un campo de ubicación, siempre es válido
    if (!this.isLocationField) {
      return true;
    }

    // Si no ha sido editado o está vacío, considerar válido
    if (!this.hasBeenEdited || !this.inputControl?.value) {
      return true;
    }

    // Si ha sido editado, debe ser seleccionado del autocompletado
    return this.isLocationSelected;
  }

}
